import { Box, Flex, HStack, Text } from '@elkaso-app/web-design';
import { formatPrice, freshness } from 'pages/prices/utils/matrix-display';
import { HeadlinePrice } from 'pages/sales-lookup/utils/headline';
import { VatMode, withVat } from 'pages/sales-lookup/utils/vat';
import { formatDerivedPrices, UomLadder } from 'utils/price-units';

export interface HeadlinePricePanelProps {
  headline: HeadlinePrice | null;
  ladder: UomLadder;
  vatMode: VatMode;
  onVatModeChange: (mode: VatMode) => void;
}

export const HeadlinePricePanel = ({ headline, ladder, vatMode, onVatModeChange }: HeadlinePricePanelProps) => {
  if (!headline) {
    return (
      <Text fontSize='14px' color='gray.500' py='20px' textAlign='center'>
        No prices yet
      </Text>
    );
  }

  const shown = withVat(headline.price, vatMode);
  const derived = formatDerivedPrices(shown, headline.tier, ladder);
  const fresh = headline.kind === 'CLOSED' ? freshness(headline.closedAt) : freshness(headline.lastModifiedAt);
  // Only strike through the public price when it's quoted in the same unit —
  // free-text catalog units are never converted.
  const showPublic =
    headline.publicPrice != null &&
    !!headline.publicPriceUnit &&
    headline.publicPriceUnit.trim().toLowerCase() === headline.unit.toLowerCase();

  return (
    <Box>
      <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.1em' color='gray.500'>
        {headline.kind === 'CLOSED' ? 'LAST CLOSED PRICE' : 'DEAL PRICE'}
      </Text>

      <Flex align='center' justify='space-between' mt='6px'>
        <HStack spacing='8px' align='baseline'>
          <Text fontSize='30px' fontWeight='extrabold' color='gray.900' lineHeight='1'>
            AED {formatPrice(shown)}
          </Text>
          <Text fontSize='14px' color='gray.500'>
            / {headline.unit}
          </Text>
        </HStack>

        <HStack spacing='0' borderRadius='full' bg='gray.100' p='3px'>
          {(['ex', 'inc'] as VatMode[]).map((mode) => (
            <Box
              key={mode}
              as='button'
              px='10px'
              py='4px'
              borderRadius='full'
              bg={vatMode === mode ? 'white' : 'transparent'}
              boxShadow={vatMode === mode ? 'sm' : 'none'}
              onClick={() => onVatModeChange(mode)}>
              <Text fontSize='11px' fontWeight='bold' color={vatMode === mode ? 'gray.800' : 'gray.500'}>
                {mode} VAT
              </Text>
            </Box>
          ))}
        </HStack>
      </Flex>

      {derived && (
        <Text fontSize='13px' fontWeight='semibold' color='red.600' mt='6px'>
          = {derived}
        </Text>
      )}

      <Text fontSize='13px' color='gray.700' mt='8px'>
        {headline.kind === 'CLOSED'
          ? `Closed ${fresh?.label ?? ''}${headline.quantity ? ` · ${headline.quantity} ${headline.unit}` : ''} · via ${
              headline.supplierName ?? 'Unknown supplier'
            }`
          : `Deal price${fresh ? ` · updated ${fresh.label}` : ''} · via ${headline.supplierName ?? 'Unknown supplier'}`}
      </Text>

      {showPublic && (
        <Text fontSize='13px' color='gray.500' mt='4px'>
          {headline.supplierName ?? 'Supplier'} public price{' '}
          <Text as='span' textDecoration='line-through'>
            AED {formatPrice(withVat(headline.publicPrice as number, vatMode))}
          </Text>
        </Text>
      )}
    </Box>
  );
};
